import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { darkTheme } from '../../lib/tokens';
import { QuotaGrantsCard } from '../quota/QuotaGrantsCard';
import {
  fetchUserDetail,
  fetchUserAudit,
  fetchUserCronEvents,
  fetchUserDevices,
  suspendUser,
  unsuspendUser,
  softDeleteUser,
  overrideSubscription,
  setUserAddon,
  triggerPasswordReset,
  changeUserRole,
} from './api';
import type {
  UserDetail,
  UserAppEntitlement,
  AuditEntry,
  CronUserEvent,
  SubscriptionTier,
  UserDevice,
  UserRole,
} from './types';
import { KNOWN_ADDONS } from './types';
import { appLabel, KNOWN_APPS, ALL_APPS, DEFAULT_APP_ID } from '../../lib/appConstants';
import { SCHOOL_SYNC_LABELS, SCHEDULE_SOURCE_LABELS } from '../../lib/schoolLabels';
import { useAppContextStore } from '../app/appContextStore';
import { PublicProfileForm } from './PublicProfileForm';
import { useCanWrite } from '../auth/permissions';
import { useAuthStore } from '../auth/authStore';
import { useStepUp } from '../auth/useStepUp';
import { useDialogs } from '../../components/PromptDialog';
import {
  SubscriptionOverrideModal,
  type SubscriptionOverrideResult,
} from './SubscriptionOverrideModal';
import { LearningTab } from './learning/LearningTab';
import { TutorChatsTab } from './learning/TutorChatsTab';

type Tab = 'profile' | 'learning' | 'tutor-chats' | 'subscription' | 'audit' | 'devices';

export function UserDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const stepUp = useStepUp();
  const dialogs = useDialogs();
  const [tab, setTab] = useState<Tab>('profile');
  const [feedback, setFeedback] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  // Offener Tier-Override-Dialog (App + aktueller Tier), null = geschlossen.
  const [subDialog, setSubDialog] = useState<{ app: string; tier: SubscriptionTier } | null>(null);

  // Per-App-Sicht aus dem globalen Admin-Picker; „Alle Apps" → currivium (Default),
  // da die per-App-Tabs (Lernprofil/Lernen/KI-Tutor/Geräte) inhärent eine App zeigen.
  const globalApp = useAppContextStore((s) => s.appId);
  const app = globalApp === ALL_APPS ? DEFAULT_APP_ID : globalApp;

  const { data: user, isLoading, error, refetch } = useQuery({
    queryKey: ['user', id, app],
    queryFn: () => fetchUserDetail(id!, app),
    enabled: !!id,
  });

  // Toast nach 4 s ausblenden
  useEffect(() => {
    if (!feedback) return;
    const t = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(t);
  }, [feedback]);

  if (!id) return <Navigate />;
  if (isLoading) return <div className="p-8 text-text-secondary">Lädt …</div>;
  if (error || !user) {
    return (
      <div className="p-8">
        <p className="text-danger">Nicht gefunden oder Fehler beim Laden.</p>
        <Link to="/users" className="text-accent-light hover:underline">
          ← Zurück zur Liste
        </Link>
      </div>
    );
  }

  async function refresh() {
    await refetch();
    queryClient.invalidateQueries({ queryKey: ['users'] });
  }

  async function handleSuspend() {
    if (!user) return;
    const reason = await dialogs.prompt({
      title: 'Nutzer sperren',
      message: `${user.email} kann sich danach nicht mehr einloggen.`,
      label: 'Grund für die Sperrung',
      multiline: true,
      confirmLabel: 'Weiter',
      destructive: true,
    });
    if (!reason) return;
    try {
      const token = await stepUp.request({
        action: 'user.suspend',
        description: `Nutzer ${user.email} sperren`,
        destructive: true,
      });
      await suspendUser(user.id, reason, token);
      setFeedback({ type: 'ok', text: 'Nutzer gesperrt.' });
      await refresh();
    } catch (err) {
      setFeedback({ type: 'err', text: humanError(err) });
    }
  }

  async function handleUnsuspend() {
    if (!user) return;
    try {
      const token = await stepUp.request({
        action: 'user.unsuspend',
        description: `Sperrung von ${user.email} aufheben`,
      });
      await unsuspendUser(user.id, token);
      setFeedback({ type: 'ok', text: 'Sperrung aufgehoben.' });
      await refresh();
    } catch (err) {
      setFeedback({ type: 'err', text: humanError(err) });
    }
  }

  async function handleSoftDelete() {
    if (!user) return;
    const ok = await dialogs.confirm({
      title: 'Konto löschen?',
      message: `Konto ${user.email} löschen? Cascade Delete in 30 Tagen.`,
      confirmLabel: 'Ja, löschen',
      destructive: true,
    });
    if (!ok) return;
    try {
      const token = await stepUp.request({
        action: 'user.softDelete',
        description: `Nutzer ${user.email} löschen`,
        destructive: true,
      });
      await softDeleteUser(user.id, token);
      setFeedback({ type: 'ok', text: 'Konto gelöscht.' });
      navigate('/users', { replace: true });
    } catch (err) {
      setFeedback({ type: 'err', text: humanError(err) });
    }
  }

  async function handleResetPassword() {
    if (!user) return;
    try {
      await triggerPasswordReset(user.id);
      setFeedback({ type: 'ok', text: 'Reset-Mail wurde getriggered.' });
    } catch (err) {
      setFeedback({ type: 'err', text: humanError(err) });
    }
  }

  async function handleChangeRole(role: UserRole) {
    if (!user || role === user.role) return;
    const reason = await dialogs.prompt({
      title: `Rolle ändern: ${user.role} → ${role}`,
      message:
        role === 'ADMIN' || role === 'ADMIN_VIEWER'
          ? 'Panel-Rollen brauchen zusätzlich einen Firebase-Account mit zweitem Faktor — ohne 2FA schlägt die Anmeldung am Panel fehl.'
          : undefined,
      label: 'Begründung (Pflicht)',
      multiline: true,
      confirmLabel: 'Rolle setzen',
      destructive: role === 'ADMIN',
    });
    if (!reason) return;
    try {
      const token = await stepUp.request({
        action: 'user.bulkAction',
        description: `Rolle von ${user.email}: ${user.role} → ${role}`,
      });
      await changeUserRole(user.id, role, reason, token);
      setFeedback({ type: 'ok', text: `Rolle auf ${role} gesetzt.` });
      await refresh();
    } catch (err) {
      setFeedback({ type: 'err', text: humanError(err) });
    }
  }

  function handleSubscriptionOverride(app: string, currentTier: SubscriptionTier) {
    if (!user) return;
    setSubDialog({ app, tier: currentTier });
  }

  async function handleSubscriptionSubmit(app: string, result: SubscriptionOverrideResult) {
    if (!user) return;
    setSubDialog(null);
    try {
      const token = await stepUp.request({
        action: 'user.subscriptionOverride',
        description: `Subscription (${appLabel(app)}) für ${user.email} → ${result.tier}`,
      });
      await overrideSubscription(user.id, app, result, token);
      setFeedback({ type: 'ok', text: `Subscription (${appLabel(app)}) gesetzt.` });
      await refresh();
    } catch (err) {
      setFeedback({ type: 'err', text: humanError(err) });
    }
  }

  async function handleSetAddon(app: string, addon: string, label: string, hasIt: boolean) {
    if (!user) return;
    const action: 'grant' | 'revoke' = hasIt ? 'revoke' : 'grant';
    const reason = await dialogs.prompt({
      title: hasIt
        ? `${label} (${appLabel(app)}) entziehen`
        : `${label} (${appLabel(app)}) freischalten`,
      message: hasIt
        ? 'Hinweis: Der Store-Kauf bleibt unberührt (kein automatischer Refund).'
        : undefined,
      label: 'Begründung (Pflicht)',
      multiline: true,
      confirmLabel: 'Weiter',
      destructive: hasIt,
    });
    if (!reason) return;
    try {
      const token = await stepUp.request({
        action: 'user.addonOverride',
        description: `${label} (${appLabel(app)}) für ${user.email} ${hasIt ? 'entziehen' : 'freischalten'}`,
        destructive: hasIt,
      });
      await setUserAddon(user.id, app, { addon, action, reason: reason.trim() }, token);
      setFeedback({
        type: 'ok',
        text: hasIt ? `${label} (${appLabel(app)}) entzogen.` : `${label} (${appLabel(app)}) freigeschaltet.`,
      });
      await refresh();
    } catch (err) {
      setFeedback({ type: 'err', text: humanError(err) });
    }
  }

  return (
    <div className="mx-auto max-w-[1200px]">
      {stepUp.modal}
      {dialogs.dialog}
      {subDialog && (
        <SubscriptionOverrideModal
          appName={appLabel(subDialog.app)}
          currentTier={subDialog.tier}
          onSubmit={(result) => void handleSubscriptionSubmit(subDialog.app, result)}
          onCancel={() => setSubDialog(null)}
        />
      )}
      {feedback && <Toast type={feedback.type} text={feedback.text} />}

      <Breadcrumb email={user.email} />

      <Header
        user={user}
        onSuspend={handleSuspend}
        onUnsuspend={handleUnsuspend}
        onResetPassword={handleResetPassword}
        onSoftDelete={handleSoftDelete}
      />

      <Tabs current={tab} onChange={setTab} />

      <div className="mt-4">
        {tab === 'profile' && <ProfileTab user={user} onChangeRole={handleChangeRole} />}
        {tab === 'learning' && (
          <LearningTab userId={user.id} app={app} enabled={tab === 'learning'} />
        )}
        {tab === 'tutor-chats' && (
          <TutorChatsTab userId={user.id} app={app} enabled={tab === 'tutor-chats'} />
        )}
        {tab === 'subscription' && (
          <SubscriptionTab
            user={user}
            onOverride={handleSubscriptionOverride}
            onSetAddon={handleSetAddon}
          />
        )}
        {tab === 'audit' && <AuditTab userId={user.id} app={app} />}
        {tab === 'devices' && (
          <DevicesTab userId={user.id} app={app} count={user._count.deviceTokens} />
        )}
      </div>

      {user.suspendedAt && (
        <SuspendedBar reason={user.suspendedReason} since={user.suspendedAt} />
      )}
    </div>
  );
}

// ── Header ─────────────────────────────────────────────────────

interface HeaderProps {
  user: UserDetail;
  onSuspend: () => void;
  onUnsuspend: () => void;
  onResetPassword: () => void;
  onSoftDelete: () => void;
}

function Header({
  user,
  onSuspend,
  onUnsuspend,
  onResetPassword,
  onSoftDelete,
}: HeaderProps): JSX.Element {
  const initial = user.email.charAt(0).toUpperCase();
  // Nur-Lesen-Rolle bekommt keine Aktionsleiste (CUR-995). Das Backend weist
  // die Aufrufe ohnehin ab — hier geht es darum, sie nicht anzubieten.
  const canWrite = useCanWrite();

  return (
    <header className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
      <div className="flex items-center gap-4">
        <div
          className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full text-xl font-bold text-white"
          style={{
            background: `linear-gradient(135deg, ${darkTheme.colors.accent}, ${darkTheme.colors.accentLight})`,
          }}
        >
          {initial}
        </div>
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-bold text-text-primary">
            {user.displayName ?? '—'}
          </h1>
          <p className="truncate text-sm text-text-secondary">{user.email}</p>
        </div>
      </div>

      {canWrite && (
        <div className="flex flex-wrap items-center gap-2 md:justify-end">
          <button type="button" onClick={onResetPassword} className={btnSecondary}>
            Passwort-Reset
          </button>
          {user.suspendedAt ? (
            <button type="button" onClick={onUnsuspend} className={btnPrimary}>
              Entsperren
            </button>
          ) : (
            <button type="button" onClick={onSuspend} className={btnWarning}>
              Sperren
            </button>
          )}
          <button type="button" onClick={onSoftDelete} className={btnDanger}>
            Löschen
          </button>
        </div>
      )}
    </header>
  );
}

const btnSecondary = 'rounded-md border border-border-subtle bg-bg-elevated px-3 py-1.5 text-xs text-text-primary hover:bg-bg-overlay';
const btnPrimary = 'rounded-md bg-accent px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90';
const btnWarning = 'rounded-md px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90';
const btnDanger = 'rounded-md px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90';

// ── Tabs ───────────────────────────────────────────────────────

function Tabs({ current, onChange }: { current: Tab; onChange: (t: Tab) => void }): JSX.Element {
  const canWrite = useCanWrite();
  const tabs: { id: Tab; label: string }[] = [
    { id: 'profile', label: 'Profil' },
    { id: 'learning', label: 'Lernen' },
    // KI-Tutor zeigt Volltext-Gespraeche — bleibt der Vollrolle vorbehalten
    // (CUR-995), passend zum ADMIN-Guard auf den tutor-sessions-Routen.
    ...(canWrite ? [{ id: 'tutor-chats' as Tab, label: 'KI-Tutor' }] : []),
    { id: 'subscription', label: 'Subscription' },
    { id: 'audit', label: 'Audit' },
    { id: 'devices', label: 'Geräte' },
  ];

  return (
    <div
      className="flex gap-4 overflow-x-auto border-b scrollbar-none"
      style={{ borderColor: darkTheme.colors.borderSubtle }}
    >
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          className="relative shrink-0 whitespace-nowrap px-1 py-2 text-sm transition-colors"
          style={{
            color: current === t.id ? darkTheme.colors.accentLight : darkTheme.colors.textSecondary,
            fontWeight: current === t.id ? 600 : 400,
          }}
        >
          {t.label}
          {current === t.id && (
            <span
              aria-hidden
              className="absolute bottom-[-1px] left-0 right-0 h-[2px]"
              style={{ background: darkTheme.colors.accent }}
            />
          )}
        </button>
      ))}
    </div>
  );
}

// ── Profile Tab ────────────────────────────────────────────────

function ProfileTab({
  user,
  onChangeRole,
}: {
  user: UserDetail;
  onChangeRole: (role: UserRole) => void;
}): JSX.Element {
  // PublicProfileForm schreibt (PATCH) — fuer die Leserolle kein Formular.
  const canWrite = useCanWrite();
  // Die eigene Rolle laesst sich nicht umstellen (das Backend lehnt es ab):
  // Wer sich selbst herabstuft, verliert den Endpunkt, mit dem er es
  // zuruecknehmen koennte. Hier gar nicht erst als Auswahl anbieten.
  const eigenerAccount = useAuthStore((s) => s.user?.id) === user.id;
  const showPublicProfile = canWrite && (user.role === 'ADMIN' || user.role === 'REVIEWER');
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Card title="Account">
          {canWrite && !eigenerAccount ? (
            <RoleField role={user.role} onChange={onChangeRole} />
          ) : (
            <Field label="Role" value={user.role} />
          )}
          <Field label="Email" value={user.email} />
          <Field label="Email bestätigt" value={user.emailVerified ? 'ja' : 'nein'} />
          <Field label="Auth-Provider" value={user.authProvider} />
          <Field label="Erstellt" value={formatDate(user.createdAt)} />
          <Field label="Zuletzt aktiv" value={user.lastSeenAt ? formatDate(user.lastSeenAt) : 'noch nie'} />
          <Field label="App-Version" value={user.lastAppVersion ?? '—'} />
        </Card>
        <Card title="Lernprofil">
          <Field label="Vorwissen" value={user.profile?.priorKnowledge ?? '—'} />
          <Field label="Std/Woche" value={user.profile?.weeklyHours?.toString() ?? '—'} />
          <Field label="Examens-Datum" value={user.profile?.targetExamDate ? formatDate(user.profile.targetExamDate) : '—'} />
          <Field label="Examens-Typ" value={user.profile?.targetExamType ?? '—'} />
        </Card>
        <Card title="Schule & Stundenplan">
          <Field label="Besucht Schule" value={user.profile?.attendsSchool ? 'ja' : 'nein'} />
          <Field
            label="Sync-Modus"
            value={user.profile?.schoolSync ? SCHOOL_SYNC_LABELS[user.profile.schoolSync] ?? user.profile.schoolSync : '—'}
          />
          <Field
            label="Schulstart"
            value={user.profile?.schoolStartDate ? formatDate(user.profile.schoolStartDate) : '—'}
          />
          <Field
            label="Schuldauer"
            value={user.profile?.schoolDurationMonths ? `${user.profile.schoolDurationMonths} Monate` : '—'}
          />
          <Field label="Stundenplan verknüpft" value={user.schoolSchedule ? 'ja' : 'nein'} />
          {user.schoolSchedule && (
            <>
              <Field
                label="Quelle"
                value={SCHEDULE_SOURCE_LABELS[user.schoolSchedule.source] ?? user.schoolSchedule.source}
              />
              <Field label="Zuletzt importiert" value={formatDate(user.schoolSchedule.extractedAt)} />
            </>
          )}
        </Card>
      </div>
      {showPublicProfile && <PublicProfileForm user={user} />}
    </div>
  );
}

// ── Apps & Abos (Cross-App-Entitlements) ───────────────────────

/**
 * Rollen-Umschalter im Account-Block.
 *
 * Der Endpunkt `PATCH /admin/users/:id/role` gab es lange, aber keine
 * Oberflaeche dazu — Rollen wurden von Hand in der DB gesetzt. Mit der
 * Nur-Lesen-Rolle (CUR-995) waere das ein Feature ohne Vergabeweg gewesen.
 */
const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: 'USER', label: 'User — App, kein Panel' },
  { value: 'ADMIN_VIEWER', label: 'Admin (nur Lesen) — Benutzer, Social, KPIs, Feedback' },
  { value: 'REVIEWER', label: 'Reviewer — Content-Review' },
  { value: 'ADMIN', label: 'Admin — volles Panel' },
];

function RoleField({
  role,
  onChange,
}: {
  role: UserRole;
  onChange: (role: UserRole) => void;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <label htmlFor="user-role" className="shrink-0 text-xs text-text-tertiary">
        Role
      </label>
      <select
        id="user-role"
        value={role}
        onChange={(e) => onChange(e.target.value as UserRole)}
        className="min-w-0 flex-1 truncate rounded-md border bg-bg-base px-2 py-1 text-right text-xs text-text-primary outline-none focus:border-accent"
        style={{ borderColor: darkTheme.colors.borderSubtle }}
      >
        {ROLE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function tierBadge(tier: SubscriptionTier): JSX.Element {
  const isPaid = tier !== 'FREE';
  return (
    <span
      className={
        isPaid
          ? 'rounded-full bg-success/15 px-2 py-0.5 text-xs font-medium text-success'
          : 'rounded-full bg-bg-elevated px-2 py-0.5 text-xs text-text-secondary'
      }
    >
      {tier}
    </span>
  );
}

// ── Subscription Tab (pro App) ─────────────────────────────────
//
// Eine Karte je bekannter App; Tier + Add-ons werden PRO APP gesetzt (HP und HPP
// unabhängig). Auch Apps ohne bestehendes Entitlement werden gezeigt (als FREE) —
// der Backend-Upsert legt das Entitlement beim Setzen an.
// docs/features/admin-user-detail-multi-app.md §5.

function emptyEntitlement(app: string): UserAppEntitlement {
  return {
    app,
    tier: 'FREE',
    platform: null,
    expiresAt: null,
    isTrialActive: false,
    trialEndsAt: null,
    firstPurchaseAt: null,
    addons: [],
    updatedAt: '',
  };
}

export function AppSubscriptionCard({
  ent,
  disabled,
  onOverride,
  onSetAddon,
}: {
  ent: UserAppEntitlement;
  disabled: boolean;
  onOverride: (app: string, currentTier: SubscriptionTier) => void;
  onSetAddon: (app: string, addon: string, label: string, hasIt: boolean) => void;
}): JSX.Element {
  const canWrite = useCanWrite();
  return (
    <Card title={appLabel(ent.app)}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {tierBadge(ent.tier)}
          {ent.isTrialActive && (
            <span className="rounded-full bg-bg-elevated px-2 py-0.5 text-xs text-text-secondary">
              Trial
            </span>
          )}
        </div>
        {canWrite && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onOverride(ent.app, ent.tier)}
            className={`${btnSecondary} disabled:cursor-not-allowed disabled:opacity-50`}
          >
            Anpassen
          </button>
        )}
      </div>

      <Field label="Plattform" value={ent.platform ?? '—'} />
      <Field label="Läuft ab" value={ent.expiresAt ? formatDate(ent.expiresAt) : 'unbegrenzt'} />
      <Field label="Trial endet" value={ent.trialEndsAt ? formatDate(ent.trialEndsAt) : '—'} />
      <Field label="Erster Kauf" value={ent.firstPurchaseAt ? formatDate(ent.firstPurchaseAt) : '—'} />

      <div className="mt-3 border-t border-border-subtle pt-3">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-text-tertiary">
          Add-ons (Einmalkäufe)
        </p>
        <div className="space-y-2">
          {KNOWN_ADDONS.map((addon) => {
            const hasIt = ent.addons.includes(addon.key);
            return (
              <div key={addon.key} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-text-primary">{addon.label}</span>
                  <span
                    className={
                      hasIt
                        ? 'rounded-full bg-success/15 px-2 py-0.5 text-xs font-medium text-success'
                        : 'rounded-full bg-bg-elevated px-2 py-0.5 text-xs text-text-secondary'
                    }
                  >
                    {hasIt ? 'freigeschaltet' : 'nicht freigeschaltet'}
                  </span>
                </div>
                {canWrite && (
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onSetAddon(ent.app, addon.key, addon.label, hasIt)}
                    className={`${hasIt ? btnDanger : btnPrimary} disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    {hasIt ? 'Entziehen' : 'Freischalten'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

export function SubscriptionTab({
  user,
  onOverride,
  onSetAddon,
}: {
  user: UserDetail;
  onOverride: (app: string, currentTier: SubscriptionTier) => void;
  onSetAddon: (app: string, addon: string, label: string, hasIt: boolean) => void;
}): JSX.Element {
  const byApp = new Map((user.apps ?? []).map((a) => [a.app, a]));
  const disabled = !!user.deletedAt;
  return (
    <div className="space-y-4">
      {KNOWN_APPS.map((known) => (
        <AppSubscriptionCard
          key={known.id}
          ent={byApp.get(known.id) ?? emptyEntitlement(known.id)}
          disabled={disabled}
          onOverride={onOverride}
          onSetAddon={onSetAddon}
        />
      ))}

      <QuotaGrantsCard userId={user.id} />

      {user.subscription?.revenueCatId ? (
        <Card title="Billing">
          <Field label="RevenueCat-ID" value={user.subscription.revenueCatId} />
        </Card>
      ) : null}
    </div>
  );
}

// ── Audit Tab ──────────────────────────────────────────────────
//
// Zwei Sektionen:
//   1. Admin-Aktionen (ContentAuditLog) — wer hat manuell etwas am User geändert
//   2. Cron-Aktivität (CronUserEvent) — was haben die Crons für diesen User getan
//
// Cron-Events bekommen einen eigenen Block mit Job-Filter, damit man gezielt
// prüfen kann ob z. B. der streakAtRisk-Cron heute Abend gelaufen ist und was
// er für den User entschieden hat.

function AuditTab({ userId, app }: { userId: string; app: string }): JSX.Element {
  return (
    <div className="space-y-6">
      {/* Admin-Aktionen (contentAuditLog) sind app-übergreifend — eine Aktion wie
          Passwort-Reset ist nicht app-spezifisch. Cron-Events sind pro App. */}
      <AdminActionsSection userId={userId} />
      <CronEventsSection userId={userId} app={app} />
    </div>
  );
}

function AdminActionsSection({ userId }: { userId: string }): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['user-audit', userId],
    queryFn: () => fetchUserAudit(userId),
  });

  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
        Admin-Aktionen
      </h3>
      {isLoading && <div className="text-text-secondary">Lädt …</div>}
      {error && <div className="text-danger">Fehler beim Laden.</div>}
      {!isLoading && !error && (!data || data.length === 0) && (
        <Card title="">Keine Admin-Aktionen für diesen User.</Card>
      )}
      {data && data.length > 0 && (
        <div className="space-y-2">
          {data.map((entry) => (
            <AuditRow key={entry.id} entry={entry} />
          ))}
        </div>
      )}
    </section>
  );
}

function CronEventsSection({ userId, app }: { userId: string; app: string }): JSX.Element {
  const [jobFilter, setJobFilter] = useState<string>('');
  const { data, isLoading, error } = useQuery({
    queryKey: ['user-cron-events', userId, app, jobFilter],
    queryFn: () =>
      fetchUserCronEvents(userId, app, {
        ...(jobFilter ? { jobName: jobFilter } : {}),
        limit: 100,
      }),
  });

  return (
    <section>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
          Cron-Aktivität
        </h3>
        {data && data.availableJobNames.length > 0 && (
          <div className="flex items-center gap-2">
            <label className="text-[11px] text-text-tertiary" htmlFor="cron-job-filter">
              Job
            </label>
            <select
              id="cron-job-filter"
              value={jobFilter}
              onChange={(e) => setJobFilter(e.target.value)}
              className="rounded-md border px-2 py-1 text-xs"
              style={{
                background: darkTheme.colors.bgElevated,
                borderColor: darkTheme.colors.borderSubtle,
                color: darkTheme.colors.textPrimary,
              }}
            >
              <option value="">Alle ({data.availableJobNames.length})</option>
              {data.availableJobNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      {isLoading && <div className="text-text-secondary">Lädt …</div>}
      {error && <div className="text-danger">Fehler beim Laden.</div>}
      {!isLoading && !error && (!data || data.events.length === 0) && (
        <Card title="">
          {jobFilter
            ? `Keine Events vom Job „${jobFilter}" für diesen User.`
            : 'Keine Cron-Aktionen für diesen User in den letzten 90 Tagen.'}
        </Card>
      )}
      {data && data.events.length > 0 && (
        <div className="space-y-2">
          {data.events.map((event) => (
            <CronEventRow key={event.id} event={event} />
          ))}
        </div>
      )}
    </section>
  );
}

function CronEventRow({ event }: { event: CronUserEvent }): JSX.Element {
  const isSkipped = event.action.endsWith('.skipped') || event.action.endsWith('.failed');
  const accent = isSkipped ? darkTheme.colors.warning : darkTheme.colors.accentLight;

  return (
    <div
      className="rounded-md border p-3 text-sm"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-xs" style={{ color: accent }}>
            {event.action}
          </span>
          {event.reason && (
            <span
              className="rounded px-1.5 py-0.5 text-[10px] font-semibold"
              style={{
                background: darkTheme.colors.bgElevated,
                color: darkTheme.colors.textSecondary,
              }}
            >
              {event.reason}
            </span>
          )}
        </div>
        <span className="text-[11px] text-text-tertiary">
          {formatRelative(event.createdAt)}
        </span>
      </div>
      <div className="mt-1 text-xs text-text-secondary">
        <span className="font-mono">{event.jobName}</span>
        {event.run && (
          <>
            <span className="mx-2 text-text-tertiary">·</span>
            <span className="text-text-tertiary">
              Run gestartet {formatRelative(event.run.startedAt)}
              {event.run.durationMs != null && ` (${event.run.durationMs.toLocaleString('de-DE')} ms)`}
            </span>
          </>
        )}
      </div>
      {event.payload && Object.keys(event.payload).length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-[11px] text-text-tertiary hover:text-text-primary">
            Payload
          </summary>
          <pre className="mt-1 overflow-auto rounded bg-bg-base p-2 font-mono text-[11px] text-text-primary">
            {JSON.stringify(event.payload, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

function AuditRow({ entry }: { entry: AuditEntry }): JSX.Element {
  return (
    <div
      className="rounded-md border p-3 text-sm"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-mono text-xs text-accent-light">{entry.action}</span>
        <span className="text-[11px] text-text-tertiary">
          {formatRelative(entry.createdAt)}
        </span>
      </div>
      <div className="mt-1 text-xs text-text-secondary">
        Von: {entry.user?.email ?? entry.user?.id ?? '—'}
        {entry.stepUpUsed && (
          <span
            className="ml-2 rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase"
            style={{ background: darkTheme.colors.accentSubtle, color: darkTheme.colors.accentLight }}
          >
            Step-up
          </span>
        )}
        {entry.ipAddress && <span className="ml-2 text-text-tertiary">{entry.ipAddress}</span>}
      </div>
      {entry.metadata?.reason && (
        <p className="mt-2 text-xs text-text-primary">
          <span className="text-text-tertiary">Begründung:</span>{' '}
          <span>{String(entry.metadata.reason)}</span>
        </p>
      )}
      {(entry.before != null || entry.after != null) && (
        <details className="mt-2">
          <summary className="cursor-pointer text-[11px] text-text-tertiary hover:text-text-primary">
            Diff
          </summary>
          <pre className="mt-1 overflow-auto rounded bg-bg-base p-2 font-mono text-[11px] text-text-primary">
            {JSON.stringify({ before: entry.before, after: entry.after }, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

// ── Devices Tab ────────────────────────────────────────────────
//
// Listet die registrierten Push-Geräte (DeviceToken). Pro Gerät haben wir nur
// Plattform, deviceId und Zeitstempel — `updatedAt` ist ein Proxy für „zuletzt
// aktiv", weil die App den Push-Token bei jedem Start re-registriert. Der volle
// Push-Token wird vom Backend nicht ausgeliefert (nur ein maskierter Ausschnitt).

function DevicesTab({
  userId,
  app,
  count,
}: {
  userId: string;
  app: string;
  count: number;
}): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['user-devices', userId, app],
    queryFn: () => fetchUserDevices(userId, app),
  });

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
          Registrierte Geräte
        </h3>
        <span className="text-xs text-text-tertiary">{count}</span>
      </div>

      {isLoading && <div className="text-text-secondary">Lädt …</div>}
      {error && <div className="text-danger">Fehler beim Laden der Geräte.</div>}
      {!isLoading && !error && (!data || data.length === 0) && (
        <Card title="">Keine Push-Geräte registriert.</Card>
      )}

      {data && data.length > 0 && (
        <div className="space-y-2">
          {data.map((device) => (
            <DeviceRow key={device.id} device={device} />
          ))}
        </div>
      )}

      <p className="text-[11px] text-text-tertiary">
        „Zuletzt aktiv" entspricht der letzten Push-Token-Registrierung (bei jedem App-Start).
      </p>
    </section>
  );
}

function DeviceRow({ device }: { device: UserDevice }): JSX.Element {
  const meta = platformMeta(device.platform);

  return (
    <div
      className="rounded-md border p-3"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex items-center gap-2">
          <span aria-hidden className="text-base">
            {meta.icon}
          </span>
          <span className="text-sm font-medium text-text-primary">{meta.label}</span>
        </div>
        <span className="text-[11px] text-text-tertiary">
          zuletzt aktiv {formatRelative(device.updatedAt)}
        </span>
      </div>
      <div className="mt-2 grid grid-cols-1 gap-1 text-xs text-text-secondary sm:grid-cols-2">
        <div className="flex gap-2">
          <span className="text-text-tertiary">Geräte-ID:</span>
          <span className="truncate font-mono">{device.deviceId ?? '—'}</span>
        </div>
        <div className="flex gap-2">
          <span className="text-text-tertiary">Push-Token:</span>
          <span className="truncate font-mono">{device.tokenPreview}</span>
        </div>
        <div className="flex gap-2">
          <span className="text-text-tertiary">Registriert:</span>
          <span>{formatDate(device.createdAt)}</span>
        </div>
      </div>
    </div>
  );
}

function platformMeta(platform: UserDevice['platform']): { icon: string; label: string } {
  switch (platform) {
    case 'IOS':
      return { icon: '', label: 'iOS' };
    case 'ANDROID':
      return { icon: '🤖', label: 'Android' };
    case 'STRIPE':
      return { icon: '💳', label: 'Web / Stripe' };
    default:
      return { icon: '📱', label: platform };
  }
}

// ── Helpers ────────────────────────────────────────────────────

function Card({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div
      className="rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
        {title}
      </h3>
      <dl className="space-y-2 text-sm">{children}</dl>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-text-secondary">{label}</dt>
      <dd className="truncate text-text-primary">{value}</dd>
    </div>
  );
}

function Breadcrumb({ email }: { email: string }): JSX.Element {
  return (
    <nav className="mb-4 text-xs text-text-tertiary">
      <Link to="/users" className="hover:text-text-primary">
        Benutzer
      </Link>
      <span className="mx-2 opacity-50">›</span>
      <span className="text-text-primary">{email}</span>
    </nav>
  );
}

function SuspendedBar({ reason, since }: { reason: string | null; since: string }): JSX.Element {
  return (
    <div
      className="sticky bottom-4 mt-6 rounded-md p-3 text-sm"
      style={{ background: darkTheme.colors.warningBg, color: darkTheme.colors.warning }}
    >
      ⚠ Suspendiert seit {formatDate(since)}.
      {reason && <span className="ml-2">Grund: {reason}</span>}
    </div>
  );
}

function Toast({ type, text }: { type: 'ok' | 'err'; text: string }): JSX.Element {
  return (
    <div
      role="status"
      className="fixed right-6 top-20 z-50 rounded-md px-4 py-2 text-sm shadow-lg"
      style={{
        background: type === 'ok' ? darkTheme.colors.successBg : darkTheme.colors.dangerBg,
        color: type === 'ok' ? darkTheme.colors.success : darkTheme.colors.danger,
      }}
    >
      {text}
    </div>
  );
}

function Navigate(): JSX.Element {
  return <div className="p-8 text-text-secondary">Lädt …</div>;
}

function humanError(err: unknown): string {
  if (err instanceof Error) return err.message;
  return 'Unbekannter Fehler';
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('de-DE');
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'gerade eben';
  if (ms < 3_600_000) return `vor ${Math.floor(ms / 60_000)} min`;
  if (ms < 86_400_000) return `vor ${Math.floor(ms / 3_600_000)} h`;
  return new Date(iso).toLocaleDateString('de-DE');
}
